import type { ApiError, ApiRequestOptions } from "./types";

// API 기본 URL
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

// 기본 헤더
const getDefaultHeaders = (): Record<string, string> => ({
  "Content-Type": "application/json",
  Accept: "application/json",
});

// API 에러 클래스
export class ApiException extends Error {
  public status: number;
  public code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiException";
    this.status = status;
    this.code = code;
  }
}

// 기본 fetch 래퍼 함수
export const apiRequest = async <T = unknown>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const { method = "GET", headers = {}, body, timeout = 10000, retries = 3 } = options;

  const url = `${API_BASE_URL}${endpoint}`;
  const requestHeaders = { ...getDefaultHeaders(), ...headers };

  // 요청 옵션 설정
  const requestOptions: RequestInit = {
    method,
    headers: requestHeaders,
    signal: AbortSignal.timeout(timeout),
  };

  // body가 있으면 추가
  if (body && method !== "GET") {
    requestOptions.body = JSON.stringify(body);
  }

  let lastError: Error | null = null;

  // 재시도 로직
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, requestOptions);

      // 응답이 성공적이지 않으면 에러 처리
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiException(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData.code
        );
      }

      // 응답 데이터 파싱
      const data = await response.json();
      return data;
    } catch (error) {
      lastError = error as Error;

      // 마지막 시도가 아니고 네트워크 에러인 경우 재시도
      if (attempt < retries && error instanceof TypeError) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        continue;
      }

      // 재시도 불가능한 에러이거나 마지막 시도인 경우
      break;
    }
  }

  throw lastError || new ApiException("Unknown error occurred", 500);
};

// GET 요청 헬퍼
export const apiGet = <T = unknown>(
  endpoint: string,
  options?: Omit<ApiRequestOptions, "method">
) => apiRequest<T>(endpoint, { ...options, method: "GET" });

// POST 요청 헬퍼
export const apiPost = <T = unknown>(
  endpoint: string,
  body?: unknown,
  options?: Omit<ApiRequestOptions, "method" | "body">
) => apiRequest<T>(endpoint, { ...options, method: "POST", body });

// PUT 요청 헬퍼
export const apiPut = <T = unknown>(
  endpoint: string,
  body?: unknown,
  options?: Omit<ApiRequestOptions, "method" | "body">
) => apiRequest<T>(endpoint, { ...options, method: "PUT", body });

// DELETE 요청 헬퍼
export const apiDelete = <T = unknown>(
  endpoint: string,
  options?: Omit<ApiRequestOptions, "method">
) => apiRequest<T>(endpoint, { ...options, method: "DELETE" });

// PATCH 요청 헬퍼
export const apiPatch = <T = unknown>(
  endpoint: string,
  body?: unknown,
  options?: Omit<ApiRequestOptions, "method" | "body">
) => apiRequest<T>(endpoint, { ...options, method: "PATCH", body });

// 에러 처리 유틸리티
export const handleApiError = (error: unknown): ApiError => {
  if (error instanceof ApiException) {
    return {
      message: error.message,
      status: error.status,
      code: error.code,
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      status: 500,
    };
  }

  return {
    message: "Unknown error occurred",
    status: 500,
  };
};

// 응답 데이터 검증 유틸리티
export const validateApiResponse = <T>(data: unknown): data is T => {
  return data !== null && typeof data === "object";
};
